import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

type CustomCollapsibleContextType = {
  expanded: boolean;
  variant: 'primary' | 'secondary';
};

const CustomCollapsibleContext = createContext<CustomCollapsibleContextType | undefined>(undefined);

function useCustomCollapsibleContext() {
  const context = useContext(CustomCollapsibleContext);
  if (!context) {
    throw new Error('CustomCollapsible components must be used within <CustomCollapsible>');
  }
  return context;
}

function CustomCollapsible({ children, variant }: { children: ReactNode, variant: 'primary' | 'secondary'; }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <CustomCollapsibleContext.Provider value={{ expanded, variant }}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        {children}
      </Collapsible>
    </CustomCollapsibleContext.Provider>
  );
}

function Trigger({ children }: { children: ReactNode }) {
  const { expanded, variant } = useCustomCollapsibleContext();
  return (
    <CollapsibleTrigger asChild>
      <div className={cn("rounded-lg shadow border flex items-center gap-3 p-3 cursor-pointer",
       variant === 'primary' ?  'bg-[#E3F2FD] border-[#E3F2FD]' : 'bg-secondary/35 border-secondary/35'
      )}>
        {children}
        <ChevronDown className={`self-end transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
    </CollapsibleTrigger>
  );
}

function Content({ children }: { children: ReactNode }) {
  const { variant } = useCustomCollapsibleContext();
  return <CollapsibleContent className={cn("rounded-b-lg", variant === 'primary' ?  'bg-[#E3F2FD] border-[#E3F2FD]' : 'bg-secondary/35 border-secondary/35')}>
    {children}
  </CollapsibleContent>;
}

// Compound pattern
CustomCollapsible.Trigger = Trigger;
CustomCollapsible.Content = Content;

export default CustomCollapsible;
